import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { Facet, loadSearchActions, loadSearchAnalyticsActions } from '@coveo/headless';
import SearchLocationHero from './LocationSearchHero/LocationSearchHero';
import LocationSearchFilterBar from './LocationSearchFilterBar/LocationSearchFilterBar';
import MapCoveoResultToCard from './mapCoveoResultToCard/mapCoveoResultToCard';
import {
  LocationSearchCoords,
  LocationSearchFacetControllers,
  LocationSearchFacetKey,
  SearchLocationHeroProps,
} from './LocationSearch.types';
import {
  getLocationSearchDistanceOptions,
  mapSitesFacetsToControllers,
  resolveDefaultDistanceSelection,
} from './locationSearchFilterConfig';
import { uniqueSiteRelatedCommunities } from './uniqueSiteRelatedCommunities';
import { scrollToSection } from '../A11y/scrollToSection/scrollToSection';
import useSitesResultList from 'lib/hooks/coveo/sitesHeadless/useSitesResultList';
import useSitesQuerySummary from 'lib/hooks/coveo/sitesHeadless/useSitesQuerySummary';
import sitesHeadlessEngine from 'lib/utils/coveo/sitesHeadless/sitesHeadlessEngine';
import { getSitesHeadlessFacets } from 'lib/utils/coveo/sitesHeadless/sitesHeadlessFacets';
import { RootState } from 'src/store/store';
import {
  clearCommunity,
  markSearchResetConsumed,
  setCommunities,
  setCoords,
  setDistanceFilter,
  toggleCommunity,
} from '../../features/locationSearchSlice';

const FACET_KEYS: LocationSearchFacetKey[] = ['condition', 'status', 'ageGroup', 'language'];

function LocationSearch(props: SearchLocationHeroProps) {
  const dispatch = useDispatch();
  const theme = useTheme();
  const isDesktopLayout = useMediaQuery(theme.breakpoints.up('lg'));
  const {
    coords,
    locationLabel,
    sortByDistance,
    selectedCommunities,
    nationwideTotal,
    communities,
    distanceLabel,
    distanceMiles,
    searchResetVersion,
    consumedSearchResetVersion,
  } = useSelector((state: RootState) => state.locationSearch);
  const [facetControllers, setFacetControllers] = useState<
    LocationSearchFacetControllers | undefined
  >(undefined);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    try {
      setFacetControllers(mapSitesFacetsToControllers(getSitesHeadlessFacets()));
    } catch {
      setFacetControllers(undefined);
    }
  }, []);

  const searchOptions = useMemo(() => {
    const options: {
      latitude?: number;
      longitude?: number;
      sortByDistance: boolean;
      communities?: string[];
      distanceMiles?: number;
    } = {
      latitude: coords?.lat,
      longitude: coords?.lng,
      sortByDistance,
      communities: selectedCommunities.length > 0 ? selectedCommunities : undefined,
    };

    if (distanceMiles != null) {
      options.distanceMiles = distanceMiles;
    }

    return options;
  }, [coords, sortByDistance, selectedCommunities, distanceMiles]);

  const { state, isLoading } = useSitesResultList(sitesHeadlessEngine, searchOptions);
  const queryTotal = useSitesQuerySummary(sitesHeadlessEngine);

  useEffect(() => {
    dispatch(setCommunities(uniqueSiteRelatedCommunities(state)));
  }, [dispatch, state]);

  const distanceOptions = useMemo(
    () => getLocationSearchDistanceOptions(props.fields),
    [props.fields]
  );

  const applyDefaultDistance = useCallback(() => {
    const selection = resolveDefaultDistanceSelection(
      props.fields?.defaultDistance,
      distanceOptions
    );
    if (!selection) {
      return;
    }
    dispatch(setDistanceFilter({ label: selection.label, miles: selection.miles ?? null }));
  }, [dispatch, props.fields?.defaultDistance, distanceOptions]);

  const clearAllFilterState = useCallback(() => {
    dispatch(setDistanceFilter({ label: null, miles: null }));
    dispatch(clearCommunity());

    FACET_KEYS.forEach((key) => {
      const facet = facetControllers?.[key] as Facet | undefined;
      if (facet?.state.hasActiveValues) {
        facet.deselectAll();
      }
    });

    if (typeof window !== 'undefined') {
      const { executeSearch } = loadSearchActions(sitesHeadlessEngine);
      const { logInterfaceChange } = loadSearchAnalyticsActions(sitesHeadlessEngine);
      sitesHeadlessEngine.dispatch(executeSearch(logInterfaceChange()));
    }
  }, [dispatch, facetControllers]);

  const handleLocationSearch = useCallback(
    (
      nextCoords: LocationSearchCoords,
      locality?: string,
      options?: { scrollToResults?: boolean }
    ) => {
      dispatch(setCoords({ ...nextCoords, locationLabel: locality }));
      // A distance already chosen this session (kept in redux) wins over the default.
      if (!distanceLabel) {
        applyDefaultDistance();
      }
      if (options?.scrollToResults !== false) {
        scrollToSection(isDesktopLayout);
      }
    },
    [dispatch, applyDefaultDistance, distanceLabel, isDesktopLayout]
  );

  const handleCommunitySelect = (nextCommunity: string) => {
    dispatch(toggleCommunity(nextCommunity));
  };

  const handleDistanceChange = useCallback(
    (nextDistanceLabel: string | null, nextDistanceMiles: number | undefined) => {
      dispatch(setDistanceFilter({ label: nextDistanceLabel, miles: nextDistanceMiles ?? null }));
    },
    [dispatch]
  );

  // One-shot clearing after resetSearch. Consumed via redux (not a ref) so a
  // remount of this page does not re-run the same reset and wipe the facets
  // that survived in the Coveo engine singleton.
  useEffect(() => {
    if (searchResetVersion === 0 || searchResetVersion === consumedSearchResetVersion) {
      return;
    }
    // "Find a Site" dispatches resetSearch before client-side navigation, so
    // this effect fires on the first render — while facetControllers is still
    // undefined (it is mapped asynchronously in the mount effect above).
    // Consuming the reset now would make facet.deselectAll() a silent no-op and
    // the stale selections in the Coveo singleton would survive. Wait for the
    // controllers; this effect re-runs as soon as they are mapped.
    if (!facetControllers) {
      return;
    }
    dispatch(markSearchResetConsumed());
    clearAllFilterState();
  }, [
    consumedSearchResetVersion,
    dispatch,
    searchResetVersion,
    clearAllFilterState,
    facetControllers,
  ]);

  const handleClearAllFilters = useCallback(() => {
    clearAllFilterState();
  }, [clearAllFilterState]);

  return (
    <>
      <SearchLocationHero
        {...props}
        nationwideTotal={nationwideTotal}
        communities={communities}
        selectedCommunities={selectedCommunities}
        onCommunitySelect={handleCommunitySelect}
        onLocationSearch={handleLocationSearch}
      />
      <LocationSearchFilterBar
        {...props}
        sitesFoundCount={queryTotal}
        hasLocationContext={Boolean(coords)}
        selectedDistanceLabel={distanceLabel}
        onDistanceChange={handleDistanceChange}
        facetControllers={facetControllers}
        hasCommunityFilter={selectedCommunities.length > 0}
        onClearAllFilters={handleClearAllFilters}
      />
      <MapCoveoResultToCard
        results={state}
        isLoading={isLoading}
        fields={props.fields}
        coords={coords}
        distanceMiles={distanceMiles ?? undefined}
        hasLocationContext={Boolean(coords)}
        searchLocality={locationLabel ?? undefined}
        onClearAllFilters={handleClearAllFilters}
      />
    </>
  );
}

export default LocationSearch;
